import { useDocumentContext } from 'components/settings/documentContext';
import { FirestoreError } from 'firebase/firestore';
import { Contract, ContractSection, CONTRACT_COLLECTION_NAME } from 'models';
import { Context, createContext, ReactNode, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ExpandedCollapsed } from './interfaces';

export const useContract = () => {
    const { contractId } = useParams();
    return useDocumentContext(CONTRACT_COLLECTION_NAME, contractId as string) as [
        Contract,
        boolean,
        FirestoreError | undefined
    ];
};

export interface ContractContextType {
    selection?: string;
    expCol: ExpandedCollapsed;
    setExpCol: (k: string, v: 'expanded' | 'collapsed') => void;
    setSelection: (_?: string) => void;
    setContractSections: (_: ContractSection[]) => void;
    contract: Contract;
    originalContract: Contract;
    setContract: (_: Contract) => void;
}
const initialState = {
    expCol: new ExpandedCollapsed({ all: 'collapsed' }),
    selection: undefined,
};

const states: { [key: string]: Context<ContractContextType | undefined> | undefined } = {};

export const getContractContext = (context?: ContractContextType) => {
    const key = 'contract';
    if (!states[key] && context) {
        states[key] = createContext<ContractContextType | undefined>(context);
    }
    return states[key] as Context<ContractContextType | undefined>;
};

interface ContractContextProviderProps {
    children: ReactNode | undefined;
}
export const ContractContextProvider = ({ children }: ContractContextProviderProps) => {
    const [contract_] = useContract();
    const [expCol, setExpCol_] = useState<ExpandedCollapsed>(initialState.expCol);
    const [selection, setSelection] = useState<string | undefined>(initialState.selection);
    const [contract, setContract] = useState(contract_);
    const setExpCol = (k: string, v: 'expanded' | 'collapsed') => {
        if (k === 'all') {
            return setExpCol_(new ExpandedCollapsed({ all: v }));
        }
        return setExpCol_(new ExpandedCollapsed({ ...expCol, [k]: v }));
    };
    const setContractSections = (contractSections: ContractSection[]) =>
        setContract(new Contract({ ...contract, contractSections }));
    const context = {
        expCol,
        setExpCol,
        selection,
        setSelection,
        setContractSections,
        contract,
        setContract,
        originalContract: contract_,
    };
    const ActualContext = getContractContext(context);
    return <ActualContext.Provider value={context}>{children}</ActualContext.Provider>;
};

export function useContractContext() {
    const context = useContext(getContractContext());
    if (context === undefined) {
        throw new Error('useDContractContext must be used within a ContractContextProvider');
    }
    return context;
}
